import React from 'react';
import { useTranslation } from 'react-i18next';
import { ImageCardsSection } from '../../components/Landing/ImageCardsSection';
import { NumberedCard } from '../../components/Landing/NumberedCard';
import { ImageContentSection } from '../../components/Landing/ImageContentSection';
import { PageHeroSection } from '../../components/Landing/PageHeroSection';
import { ButtonNew } from '../../components/ButtonNew';
import { FaqSection } from '../../components/Landing/FaqSection';
import { CtaSection } from '../../components/Landing/CtaSection';
import { useDocumentTitle } from '../../shared/use-document-title';
import { uiConfig } from '../../app-ui-config';
import believeSrc from '../../assets/images/about-us/believe.webp';
import ourMissionSrc from '../../assets/images/start/our-mission.webp';
import ourMissionMobileSrc from '../../assets/images/start/our-mission-mobile.webp';
import aboutUsHeroSrc from '../../assets/images/start/about-us-hero.webp';
import aboutUsHeroMobileSrc from '../../assets/images/start/about-us-hero-mobile.webp';
import styles from './AboutUsPage.css';

const BELIEFS = [
    {
        title: 'Every story matters',
        description: 'Not just the milestones. The quiet, everyday moments are often the ones people miss most.'
    },
    {
        title: 'No writing required',
        description: 'Speak naturally, the way you’d tell a story to a friend. Boundly turns it into a clear, readable chapter.'
    },
    {
        title: 'Private by default',
        description: 'Your stories are yours. Nothing is shared with anyone unless you choose to share it.'
    },
    {
        title: 'Built to last',
        description: 'Your Digital Family Legacy Book stays yours to revisit, even if your plan ever changes.'
    }
];

function AboutUsPage() {
    const { t } = useTranslation();

    useDocumentTitle(t('aboutUsPage.documentTitle'), false);

    return (
        <>
            <PageHeroSection
                title='Every life deserves to be remembered'
                subtitle="Some stories only exist in someone's memory — a mother's childhood in a small town, a father's first job, a grandmother's story of how she met her husband. We hear pieces of them at holidays and family dinners, and we always mean to ask more. Boundly is how we make sure that happens before the moment passes."
                imageSrc={aboutUsHeroSrc}
                imageMobileSrc={aboutUsHeroMobileSrc}
                imageAlt='An older man and a young woman looking through a photo album together'
            />
            <ImageContentSection
                imageSrc={ourMissionSrc}
                imageMobileSrc={ourMissionMobileSrc}
                imageAlt='Woman smiling while looking at her phone on a couch'
            >
                <h2 className={styles.missionTitle}>Our mission</h2>
                <p className={styles.missionSubtitle}>
                    <span>We've watched people put off recording a parent's story for “someday,” only to realize later that someday never came. A life story rarely disappears all at once — it slips away one unanswered question at a time.</span>
                    Our mission is to make preserving a life story as natural as a conversation: no writing required, no blank page, no pressure to get it perfect the first time. We remove the friction that keeps these stories untold, so telling them becomes a normal part of family life — not a project for when there's time.
                </p>
                <ButtonNew
                    type='pill'
                    label='Get started'
                    link={{
                        to: uiConfig.prelanderLink,
                        target: '_blank'
                    }}
                    className={styles.missionButton}
                />
            </ImageContentSection>
            <ImageCardsSection
                title='What we believe'
                imageSrc={believeSrc}
                withTestimonials
            >
                {BELIEFS.map((belief, index) => (
                    <NumberedCard
                        key={belief.title}
                        number={index + 1}
                        title={belief.title}
                        description={belief.description}
                    />
                ))}
            </ImageCardsSection>
            <CtaSection />
            <FaqSection />
        </>
    );
}

export {
    AboutUsPage
};
